// store/filterStore.js
import { makeAutoObservable, runInAction } from 'mobx'
import objectStore from './objectStore'
import { config } from '../config'
import requestStore from './requestStore'
import logger from '../utils/logger'
import { searchUnavailableMessage } from '../utils/searchUnavailable'
import apiClient from '../utils/apiClient'
import i18nStore from './i18nStore'
import { registerTenantScopedReset } from '../tenancy/tenantScopedState'

// управление фильтрами
class FilterStore {
	searchError = null
	objectPageControls = {
		saleType: 'all',
		activeCategoryId: null, // Активная категория
		selectedSubcategories: [], // Выбранные подкатегории
		rentType: [], // Массив значений, например ['longTerm', 'daily']
	}
	address = {}
	isAddressSelected = false

	extraFilter = {} // Дополнительные фильтры
	customFieldFilters = []
	addressText = '' // текстовое представление адреса
	
	// Фильтр видимости объектов: мои, коллег, общедоступные
	objectVisibilityFilter = {
		my: true,      // Мои объекты
		colleagues: true,  // Объекты коллег
		public: true     // Общедоступные объекты
	}
	// Фильтр видимости объектов в Заявке: мои, коллег, общедоступные
	objectVisibilityFilterRequest = {
		my: true,      // Мои объекты
		colleagues: true,  // Объекты коллег
		public: true     // Общедоступные объекты
	}

	constructor() {
		makeAutoObservable(this)
		// These controls shape Organization queries, so shared browser values
		// are retired rather than restored into a newly selected Organization.
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem('objectVisibilityFilter')
			localStorage.removeItem('objectVisibilityFilterRequest')
		}
	}

	clearOrganizationState() {
		this.searchError = null
		this.objectPageControls = { saleType: 'all', activeCategoryId: null, selectedSubcategories: [], rentType: [] }
		this.address = {}
		this.isAddressSelected = false
		this.extraFilter = {}
		this.customFieldFilters = []
		this.addressText = ''
		this.objectVisibilityFilter = { my: true, colleagues: true, public: true }
		this.objectVisibilityFilterRequest = { my: true, colleagues: true, public: true }
		if (typeof localStorage !== 'undefined') {
			localStorage.removeItem('objectVisibilityFilter')
			localStorage.removeItem('objectVisibilityFilterRequest')
		}
	}
	
	// Загрузка состояния фильтра видимости из localStorage
	loadObjectVisibilityFromLocalStorage() {
		try {
			const saved = localStorage.getItem('objectVisibilityFilter')
			if (saved) {
				const parsed = JSON.parse(saved)
				// Проверяем, что в сохраненном объекте есть все необходимые поля
				if (typeof parsed === 'object' && parsed !== null) {
					this.objectVisibilityFilter = {
						my: parsed.my !== undefined ? parsed.my : true,
						colleagues: parsed.colleagues !== undefined ? parsed.colleagues : true,
						public: parsed.public !== undefined ? parsed.public : true
					}
				}
			}
		} catch (error) {
			logger.error('Ошибка при загрузке фильтра видимости:', error)
			// При ошибке устанавливаем все опции как выбранные
			this.objectVisibilityFilter = { my: true, colleagues: true, public: true }
		}
	}
	
	// Загрузка состояния фильтра видимости запросов из localStorage
	loadObjectVisibilityRequestFromLocalStorage() {
		try {
			const saved = localStorage.getItem('objectVisibilityFilterRequest')
			if (saved) {
				const parsed = JSON.parse(saved)
				// Проверяем, что в сохраненном объекте есть все необходимые поля
				if (typeof parsed === 'object' && parsed !== null) {
					this.objectVisibilityFilterRequest = {
						my: parsed.my !== undefined ? parsed.my : true,
						colleagues: parsed.colleagues !== undefined ? parsed.colleagues : true,
						public: parsed.public !== undefined ? parsed.public : true
					}
				}
			}
		} catch (error) {
			logger.error('Ошибка при загрузке фильтра видимости запросов:', error)
			// При ошибке устанавливаем все опции как выбранные
			this.objectVisibilityFilterRequest = { my: true, colleagues: true, public: true }
		}
	}
	
	// Сохранение состояния фильтра видимости в localStorage
	saveObjectVisibilityToLocalStorage() {
		try {
			// Intentionally memory-only; see clearOrganizationState().
		} catch (error) {
			logger.error('Ошибка при сохранении фильтра видимости:', error)
		}
	}
	
	// Сохранение состояния фильтра видимости запросов в localStorage
	saveObjectVisibilityRequestToLocalStorage() {
		try {
			// Intentionally memory-only; see clearOrganizationState().
		} catch (error) {
			logger.error('Ошибка при сохранении фильтра видимости запросов:', error)
		}
	}
	
	// Переключение опции фильтра видимости
	toggleObjectVisibility(key) {
		if (Object.prototype.hasOwnProperty.call(this.objectVisibilityFilter, key)) {
			this.objectVisibilityFilter[key] = !this.objectVisibilityFilter[key]
			this.saveObjectVisibilityToLocalStorage()
		}
	}
	
	// Переключение опции фильтра видимости запросов
	toggleObjectVisibilityRequest(key) {
		if (Object.prototype.hasOwnProperty.call(this.objectVisibilityFilterRequest, key)) {
			this.objectVisibilityFilterRequest[key] = !this.objectVisibilityFilterRequest[key]
			this.saveObjectVisibilityRequestToLocalStorage()
		}
	}
	
	// Установка значения опции фильтра видимости
	setObjectVisibility(key, value) {
		if (Object.prototype.hasOwnProperty.call(this.objectVisibilityFilter, key)) {
			this.objectVisibilityFilter[key] = value
			this.saveObjectVisibilityToLocalStorage()
		}
	}
	
	// Установка значения опции фильтра видимости запросов
	setObjectVisibilityRequest(key, value) {
		if (Object.prototype.hasOwnProperty.call(this.objectVisibilityFilterRequest, key)) {
			this.objectVisibilityFilterRequest[key] = value
			this.saveObjectVisibilityRequestToLocalStorage()
		}
	}

	// Добавьте метод для установки текстового значения
	setAddressText(text) {
		this.addressText = text
	}

	setAddress(adr) {
		this.address = adr
		this.isAddressSelected = adr !== null
	}

	// метод для сброса флага
	resetAddressSelection() {
		this.isAddressSelected = false
	}

	clearFilters() {
		runInAction(() => {
			this.objectPageControls = {
				saleType: 'all',
				activeCategoryId: null,
				selectedSubcategories: [],
				rentType: [],
			}
		})
	}

	// Метод для очистки дополнительных фильтров
	clearExtraFilter() {
		runInAction(() => {
			this.extraFilter = {}
		})
	}

	setCustomFieldFilters(filters) {
		this.customFieldFilters = Array.isArray(filters) ? filters : []
	}

	updateSelectedFields() {
		const selectedSubcategories = this.objectPageControls.selectedSubcategories

		logger.log('🎀 selectedSubcategories:', selectedSubcategories)

		if (!selectedSubcategories || selectedSubcategories.length === 0) {
			// Не сбрасываем фильтр, а просто выходим
			return
		}

		const fieldSets = selectedSubcategories.map(subcategory => {
			logger.log('🎇 subcategory.id:', subcategory.id)

			// Поиск JSON-файла с проверкой наличия type.id
			const jsonFile = objectStore.jsonfiles.find(
				file => file.type && file.type.id === subcategory.id
			)

			/* logger.log('🎀 jsonFile:', JSON.stringify(jsonFile)) */

			return jsonFile && jsonFile.fields ? Object.keys(jsonFile.fields) : []
		})

		logger.log('🎀 fieldSets:', fieldSets)

		if (fieldSets.length === 0 || fieldSets.some(set => set.length === 0)) {
			// Не сбрасываем фильтр, а просто выходим
			return
		}

		// Пересекаем ключи всех полей
		const commonFields = fieldSets.reduce((common, fieldSet) =>
			common.filter(field => fieldSet.includes(field))
		)

		logger.log('🎀 commonFields:', commonFields)

		// Создаем объект с описанием общих полей
		const commonFieldDetails = commonFields.reduce((result, fieldKey) => {
			const fieldData = selectedSubcategories
				.map(subcategory => {
					const jsonFile = objectStore.jsonfiles.find(
						file => file.type && file.type.id === subcategory.id
					)
					return jsonFile && jsonFile.fields ? jsonFile.fields[fieldKey] : null
				})
				.find(Boolean) // Берем описание из первой подкатегории, где поле есть
			if (fieldData) result[fieldKey] = fieldData
			return result
		}, {})

		logger.log('🎀 commonFieldDetails:', commonFieldDetails)

		// Вместо перезаписи всего extraFilter, инициализируем только отсутствующие поля
		// Не изменяем существующие значения
		if (!this.extraFilter) {
			this.extraFilter = {}
		}
		
		// Только добавляем поля, которых еще нет в extraFilter
		Object.keys(commonFieldDetails).forEach(key => {
			if (!(key in this.extraFilter)) {
				this.extraFilter[key] = commonFieldDetails[key];
			}
		});
	}

	setSaleType(v) {
		logger.log(`Установлено: ${v}`)

		this.objectPageControls.saleType = v
	}
	setRentType(option) {
		logger.log('🎞 option: ', option)
		// Если значение уже есть в массиве, удаляем его, иначе добавляем
		if (this.objectPageControls.rentType.includes(option)) {
			this.objectPageControls.rentType =
				this.objectPageControls.rentType.filter(item => item !== option)
		} else {
			this.objectPageControls.rentType.push(option)
		}
	}
	setActiveCategoryId(id) {
		this.objectPageControls.activeCategoryId = id
		this.objectPageControls.selectedSubcategories = [] // Сброс подкатегорий
	}
	toggleSubcategory(subcategory) {
		const index = this.objectPageControls.selectedSubcategories.findIndex(
			sub => sub.id === subcategory.id
		)
		if (index > -1) {
			// Удаляем подкатегорию
			this.objectPageControls.selectedSubcategories.splice(index, 1)
		} else {
			// Добавляем подкатегорию
			this.objectPageControls.selectedSubcategories.push(subcategory)
		}
	}

	fetchFOFromApp() {
		logger.log(`🍡 fetchFOFromApp`)
		const country = 'RU'
		const source = 'app' // пришло из app
		this.fetchFilteredObjectsElastic(
			1,
			this.objectPageControls,
			this.extraFilter,
			country,
			source,
			30, // добавляем дефолтный лимит
			this.address,
			this.objectVisibilityFilter
		)
	}

	fetchFOfromObjectPage() {
		logger.log(`🍡 fetchFOfromObjectPage`)

		const country = 'RU'
		const source = 'objectPage' // пришло из objectPage
		const limit = 30
		this.fetchFilteredObjectsElastic(
			1,
			this.objectPageControls,
			this.extraFilter,
			country,
			source,
			limit,
			this.address,
			this.objectVisibilityFilter
		)
	}
	fetchFOfromNewRequest(page) {
		logger.log(`🍡 fetchFOfromNewRequest`)

		const country = 'RU'
		const source = 'newRequest' // пришло из новой заявки
		const limit = 30
		this.fetchFilteredObjectsElasticForRequest(
			page,
			//this.objectPageControls,
			requestStore.filterForRequest,
			requestStore.extraFilter,
			country,
			source,
			limit,
			requestStore.address,
			this.objectVisibilityFilterRequest
		)
	}

	fetchFOFromPagination(page) {
		logger.log(`🍡 fetchFOFromPagination`)
		const country = 'RU'
		const source = 'pagination' // пришло из пагинации
		const limit = 30
		this.fetchFilteredObjectsElastic(
			page,
			this.objectPageControls,
			this.extraFilter,
			country,
			source,
			limit,
			this.address,
			this.objectVisibilityFilter
		)
	}

	fetchFOElastic() {
		logger.log(`🍡 fetchFOElastic`)
		const country = 'RU'
		const source = 'none' //
		this.fetchFilteredObjectsElastic(
			1,
			this.objectPageControls,
			this.extraFilter,
			country,
			source,
			30, // добавляем дефолтный лимит
			this.address,
			this.objectVisibilityFilter
		)
	}

	// 11111111
	fetchFOfromObjectPageConfirmBtn() {
		logger.log(`🍡 fetchFOfromObjectPageConfirmBtn`)
		const country = 'RU'
		const source = 'none' //
		logger.log(JSON.stringify(this.address))

		this.fetchFilteredObjectsElastic(
			1,
			this.objectPageControls,
			this.extraFilter,
			country,
			source,
			30,
			this.address,
			this.objectVisibilityFilter
		)
	}

	

	// ОСНОВНАЯ ФУНКЦИЯ ЗАПРОС
	async fetchFilteredObjectsElastic(
		page,
		objectPageControls,
		extraFilter,
		country,
		source,
		limit,
		address,
		objectVisibilityFilter
	) {
		logger.log('🎈💥💥💥💥💥💥💥💥  fetchFilteredObjectsElastic')
		logger.log(`🚦 objectVisibilityFilter`, JSON.stringify(objectVisibilityFilter))
		let jsonFileName = ''
		logger.log('🧵 page: ', page) //
		logger.log(`limit: ${limit}`)
		/* logger.log(`address: ${JSON.stringify(address)}`) */

		if (
			objectPageControls.selectedSubcategories.length === 1 && // Проверяем, что массив содержит ровно одно значение
			Object.keys(extraFilter).length > 0 // Проверяем, что объект не пуст
		) {
			jsonFileName = objectPageControls.selectedSubcategories[0].fields
			logger.log(
				'Условие выполнено: одна подкатегория выбрана и extraFilter не пуст'
			)
		} else {
			logger.log('Условие не выполнено')
		}

		logger.log(`💛 jsonFileName: ${jsonFileName}`)

		// The selected Organization is derived by the authenticated API client.
		const queryParams = {
			saleType: objectPageControls.saleType,
			activeCategoryId: this.objectPageControls.activeCategoryId,
			subcategories: JSON.stringify(
				objectPageControls.selectedSubcategories.map(sub => sub.id)
			),
			extraFilter: JSON.stringify(extraFilter), // Сериализуем в строку JSON
			customFieldFilters: JSON.stringify(this.customFieldFilters),
			page,
			country: i18nStore.currentCountry,
			address: JSON.stringify(address),
			limit, // Статический лимит для совместимости с сервером
			jsonFileName,
			objectVisibility: JSON.stringify(objectVisibilityFilter), // Добавляем фильтр видимости
		}
		
		// Добавляем rentType как массив параметров
		if (objectPageControls.rentType && objectPageControls.rentType.length > 0) {
			queryParams.rentType = objectPageControls.rentType
		}

		try {
			this.searchError = null
			const response = await apiClient.get('/objects', { params: queryParams })
			const data = response.data

			runInAction(() => {
				if (source === 'newRequest') {
					objectStore.objectsForRequest = data.objects // Сохраняем объекты
					objectStore.currentPageRequest = data.page // Текущая страница
					objectStore.totalPagesRequest = Math.ceil(data.totalCount / limit) // Используем переданный лимит
				} else {
					objectStore.objects = data.objects // Сохраняем объекты
					objectStore.currentPage = data.page // Текущая страница
					objectStore.totalPages = Math.ceil(data.totalCount / limit) // Используем переданный лимит
				}
			})
		} catch (err) {
			runInAction(() => {
				this.searchError = searchUnavailableMessage(err)
				if (source === 'newRequest') objectStore.objectsForRequest = []
				else objectStore.objects = []
			})
			logger.error('Error fetching filtered objects:', err.message)
			throw err
		}
	}

	// фильтр за заявки
	async fetchFilteredObjectsElasticForRequest(
		page,
		objectPageControls,
		extraFilter,
		country,
		source,
		limit,
		address,
		objectVisibilityFilter
	) {
		logger.log('🎈 ENDP-30 - fetchFilteredObjectsElasticForRequest')
		// эта функция работает только с заявками, и берет данные из requestStore фильтров
		let jsonFileName = ''
		logger.log('🧵 page: ', page) //
		logger.log(`limit: ${limit}`)
		logger.log(`🥞 fetchFilteredObjectsElasticForRequest`)

		if (
			requestStore.filterForRequest.selectedSubcategories.length === 1 && // Проверяем, что массив содержит ровно одно значение
			Object.keys(extraFilter).length > 0 // Проверяем, что объект не пуст
		) {
			jsonFileName =
				requestStore.filterForRequest.selectedSubcategories[0].fields
			logger.log(
				'Условие выполнено: одна подкатегория выбрана и extraFilter не пуст'
			)
		} else {
			logger.log('Условие не выполнено')
		}

		logger.log(`💛 jsonFileName: ${jsonFileName}`)

		// The selected Organization is derived by the authenticated API client.
		const queryParams = {
			saleType: requestStore.filterForRequest.saleType,
			activeCategoryId: requestStore.filterForRequest.activeCategoryId,
			subcategories: JSON.stringify(
				requestStore.filterForRequest.selectedSubcategories.map(sub => sub.id)
			),
			extraFilter: JSON.stringify(requestStore.extraFilter), // Сериализуем в строку JSON
			page,
			address: JSON.stringify(address),
			country: i18nStore.currentCountry,
			limit, // Статический лимит для совместимости с сервером
			jsonFileName,
			objectVisibility: JSON.stringify(objectVisibilityFilter), // Добавляем фильтр видимости
		}
		
		// Добавляем rentType как массив параметров, если они есть
		if (requestStore.filterForRequest.rentType && requestStore.filterForRequest.rentType.length > 0) {
			queryParams.rentType = requestStore.filterForRequest.rentType
		}

		try {
			this.searchError = null
			const response = await apiClient.get('/objects', { params: queryParams })
			const data = response.data

			runInAction(() => {
				if (source === 'newRequest') {
					objectStore.objectsForRequest = data.objects // Сохраняем объекты
					objectStore.currentPageRequest = data.page // Текущая страница
					objectStore.totalPagesRequest = Math.ceil(data.totalCount / limit) // Используем переданный лимит
				} else {
					objectStore.objects = data.objects // Сохраняем объекты
					objectStore.currentPage = data.page // Текущая страница
					objectStore.totalPages = Math.ceil(data.totalCount / limit) // Используем переданный лимит
				}
			})
		} catch (err) {
			runInAction(() => {
				this.searchError = searchUnavailableMessage(err)
				if (source === 'newRequest') objectStore.objectsForRequest = []
				else objectStore.objects = []
			})
			logger.error('Error fetching filtered objects:', err.message)
			throw err
		}
	}


}

const filterStore = new FilterStore()
registerTenantScopedReset('object-search-and-filters', () => filterStore.clearOrganizationState())
export default filterStore
